import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  CalendarClock,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Mail,
  Play,
  Send,
  Upload,
  Users,
  AlertTriangle,
  Lock,
  X,
} from 'lucide-react';
import { api, ApiError } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import {
  collectVariableValues,
  CreateEmailSendSchema,
  initialVariableInputs,
  parseVariableDefinitions,
  validateVariableValues,
  variableInputErrors,
  variablePlaceholder,
  type EmailList,
  type EmailSend,
  type EmailSender,
  type EmailSendPreview,
  type CsvSendRowError,
  type VariableDefinitions,
} from '@blogforge/shared';

// ─────────────────────────────────────────────────────────────────
// Send — queue an email to an audience
//
// An email is content; this page *queues a send* of it: it writes the send
// row and snapshots the selected lists' subscribers into it as queued
// recipients (POST /api/email-sends). Delivery itself belongs to the
// background sender job, so "queued" here means "the audience is frozen and
// the worker will get to it" — the recent-sends list below is where the
// progress shows up.
// ─────────────────────────────────────────────────────────────────

/** A send's status, mapped onto the badge styles globals.css defines. */
const STATUS_CLASS: Record<EmailSend['status'], string> = {
  queued: 'badge-scheduled',
  sending: 'badge-sending',
  sent: 'badge-sent',
  failed: 'badge-failed',
  cancelled: 'badge-archived',
};

function formatWhen(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A datetime-local value (local time) → the ISO instant the API expects. */
function localInputToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Why this Reply-To cannot be used, or null when it can — including when it is
 * empty, which is not an error but the absence of an override.
 *
 * The rule is the API's own: `replyTo` is validated by `CreateEmailSendSchema`
 * on both queueing routes, so the form refuses exactly what the server would
 * refuse and says it in the server's words.
 */
export function replyToProblem(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = CreateEmailSendSchema.shape.replyTo.safeParse(trimmed);
  if (parsed.success) return null;
  return parsed.error.issues[0]?.message ?? 'Invalid reply-to address';
}

export function EmailSendPage() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);

  const [emailId, setEmailId] = useState(params.get('emailId') ?? '');
  const [listIds, setListIds] = useState<string[]>([]);
  const [senderId, setSenderId] = useState('');
  /** Optional Reply-To override; '' means "let replies go to the sender". */
  const [replyTo, setReplyTo] = useState('');
  const [replyToError, setReplyToError] = useState<string | null>(null);
  const [scheduledInput, setScheduledInput] = useState('');
  const [variableInputs, setVariableInputs] = useState<Record<string, string>>({});
  const [variableErrors, setVariableErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<
    | { kind: 'idle' }
    | {
        kind: 'queued';
        recipients: number;
        duplicates: number;
        scheduledAt: string | null;
        /** Present when the audience came from a file. */
        csv?: { file: string; rows: number; duplicatesInFile: number; audience: string };
      }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Where the audience comes from. Both modes queue the same kind of send; only
  // the audience differs, which is why everything below the picker is shared.
  const [audienceMode, setAudienceMode] = useState<'lists' | 'csv'>('lists');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  /** Per-row refusals from the server, when a file could not be sent as it was. */
  const [csvRowErrors, setCsvRowErrors] = useState<CsvSendRowError[] | null>(null);
  const [csvMissingColumns, setCsvMissingColumns] = useState<string[] | null>(null);

  // ── What the form is built from ──────────────────────────────
  const emails = useQuery({
    queryKey: ['emails', { forSend: true }],
    queryFn: () => api.emails.list({ limit: 100 }),
    staleTime: 30_000,
  });
  const lists = useQuery({
    queryKey: ['email-lists', activeWorkspaceId],
    queryFn: () => api.emailLists.list(),
    enabled: Boolean(activeWorkspaceId),
    staleTime: 30_000,
  });
  const senders = useQuery({
    queryKey: ['email-senders', activeWorkspaceId],
    queryFn: () => api.emailSenders.list(),
    enabled: Boolean(activeWorkspaceId),
    staleTime: 30_000,
  });
  const recent = useQuery({
    queryKey: ['email-sends', { page: 1 }],
    queryFn: () => api.emailSends.list({ limit: 8 }),
    enabled: Boolean(activeWorkspaceId),
    staleTime: 10_000,
  });

  const emailRows = ((emails.data as { data?: Record<string, unknown>[] } | undefined)?.data ??
    []) as Record<string, unknown>[];
  const listRows: EmailList[] = lists.data?.data ?? [];
  // Only SMTP senders can deliver today; a Brevo row would be refused by the API.
  const senderRows: EmailSender[] = (senders.data?.data ?? []).filter(
    (sender) => sender.senderType === 'smtp',
  );

  // The selected email's own variable definitions — the merge fields it must
  // be given values for, on top of whatever the audience provides per contact.
  const selected = useQuery({
    queryKey: ['emails', emailId],
    queryFn: () => api.emails.get(emailId),
    enabled: Boolean(emailId),
    staleTime: 30_000,
  });
  const definitions: VariableDefinitions = useMemo(
    () =>
      parseVariableDefinitions(
        (selected.data as { variableDefinition?: unknown } | undefined)?.variableDefinition,
      ),
    [selected.data],
  );
  const variableNames = Object.keys(definitions).sort();

  // Default to the workspace's active sender once the list arrives.
  useEffect(() => {
    if (senderId) return;
    const active = senderRows.find((sender) => sender.isActive) ?? senderRows[0];
    if (active) setSenderId(active.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senders.data]);

  // Seed the variable inputs from the definitions' defaults, once per email.
  useEffect(() => {
    setVariableInputs(initialVariableInputs(definitions));
    setVariableErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, selected.data]);

  // Keep the deep link in step with the selection, so a reload (or a shared
  // link from the email list) reopens the same send.
  useEffect(() => {
    const current = params.get('emailId') ?? '';
    if (current === emailId) return;
    const next = new URLSearchParams(params);
    if (emailId) next.set('emailId', emailId);
    else next.delete('emailId');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId]);

  const variables = useMemo(
    () => collectVariableValues(definitions, variableInputs),
    [definitions, variableInputs],
  );

  const canPreview = Boolean(emailId) && listIds.length > 0;

  // Where a reply lands when nothing overrides it: the address the chosen
  // sender sends as, which is what a mail client fills in by default.
  const selectedSenderData = (senderRows.find((entry) => entry.id === senderId)?.data ??
    {}) as Record<string, unknown>;
  const senderFrom = String(selectedSenderData['from'] ?? selectedSenderData['user'] ?? '');
  const preview = useQuery({
    queryKey: ['email-sends', 'preview', emailId, listIds.join(','), JSON.stringify(variables)],
    queryFn: () =>
      api.emailSends.preview({
        emailId,
        listIds,
        ...(Object.keys(variables).length > 0 ? { variables } : {}),
      }),
    // A CSV send's audience is measured by the upload itself, not by a preview
    // call — there is nothing here to preview until the file arrives.
    enabled: canPreview && audienceMode === 'lists',
    staleTime: 5_000,
    retry: false,
  });

  const queue = useMutation({
    mutationFn: () =>
      api.emailSends.create({
        emailId,
        senderId,
        listIds,
        ...(scheduledInput ? { scheduledAt: localInputToIso(scheduledInput) } : {}),
        ...(Object.keys(variables).length > 0 ? { variables } : {}),
        ...(replyTo.trim() ? { replyTo: replyTo.trim() } : {}),
      }),
    onSuccess: (result) => {
      setFeedback({
        kind: 'queued',
        recipients: result.recipientCount,
        duplicates: result.duplicates,
        scheduledAt: result.send.scheduledAt,
      });
      void qc.invalidateQueries({ queryKey: ['email-sends'] });
      void qc.invalidateQueries({ queryKey: ['emails'] });
    },
    onError: (err: unknown) => {
      setFeedback({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    },
  });

  /**
   * Queue a CSV send. The file goes up whole and the server validates all of it
   * before queueing anything, so a 422 means *nothing* was queued — and its body
   * says which rows or which columns were the problem.
   */
  const queueCsv = useMutation({
    mutationFn: () =>
      api.emailSends.sendCsv(csvFile!, {
        emailId,
        senderId,
        ...(scheduledInput ? { scheduledAt: localInputToIso(scheduledInput) } : {}),
        ...(replyTo.trim() ? { replyTo: replyTo.trim() } : {}),
      }),
    onSuccess: (result) => {
      setCsvRowErrors(null);
      setCsvMissingColumns(null);
      setFeedback({
        kind: 'queued',
        recipients: result.recipientCount,
        duplicates: result.duplicates,
        scheduledAt: result.send.scheduledAt,
        csv: {
          file: result.send.csvFilename ?? csvFile?.name ?? 'upload',
          rows: result.accepted,
          duplicatesInFile: result.duplicatesInFile,
          audience: result.audience.name,
        },
      });
      setCsvFile(null);
      void qc.invalidateQueries({ queryKey: ['email-sends'] });
      void qc.invalidateQueries({ queryKey: ['emails'] });
    },
    onError: (err: unknown) => {
      setCsvRowErrors(null);
      setCsvMissingColumns(null);

      if (err instanceof ApiError && err.status === 422) {
        const body = (err.body ?? {}) as { errors?: CsvSendRowError[]; missingVariables?: string[] };
        if (body.missingVariables?.length) setCsvMissingColumns(body.missingVariables);
        else if (body.errors?.length) setCsvRowErrors(body.errors);
      }

      setFeedback({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    },
  });

  const cancelSend = useMutation({
    mutationFn: (id: string) => api.emailSends.cancel(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['email-sends'] }),
  });

  const resumeSend = useMutation({
    mutationFn: (id: string) => api.emailSends.resume(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['email-sends'] }),
  });

  const toggleList = (id: string) =>
    setListIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFeedback({ kind: 'idle' });

    // Both audiences queue through a route with the same Reply-To rule, so it
    // is checked once, before either of them is attempted.
    const replyToIssue = replyToProblem(replyTo);
    if (replyToIssue) {
      setReplyToError(replyToIssue);
      setFeedback({ kind: 'error', message: `Reply-To: ${replyToIssue}` });
      return;
    }
    setReplyToError(null);

    if (audienceMode === 'csv') {
      if (!emailId || !senderId) {
        setFeedback({ kind: 'error', message: 'Pick an email and a sender.' });
        return;
      }
      if (!csvFile) {
        setFeedback({ kind: 'error', message: 'Choose the CSV file to send to.' });
        return;
      }
      queueCsv.mutate();
      return;
    }

    if (variableNames.length > 0) {
      const checked = validateVariableValues(definitions, variables, false);
      if (!checked.success) {
        setVariableErrors(variableInputErrors(checked.errors));
        setFeedback({
          kind: 'error',
          message: 'Some variable values need fixing — see the fields in the audience panel.',
        });
        return;
      }
      setVariableErrors({});
    }

    if (!emailId || !senderId || listIds.length === 0) {
      setFeedback({ kind: 'error', message: 'Pick an email, a sender and at least one list.' });
      return;
    }

    queue.mutate();
  };

  const previewData: EmailSendPreview | undefined = preview.data;
  const missingVariables = previewData?.missingVariables ?? [];
  const blocking = missingVariables.length > 0;
  const busy = queue.isPending || queueCsv.isPending;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="shrink-0 px-8 pt-8 pb-6">
        <p className="kicker mb-2">
          <span className="bg-accent h-1.5 w-1.5 rounded-full" />
          Outreach
        </p>
        <h1 className="text-ink text-[22px] font-bold tracking-tight">Send</h1>
        <p className="text-ink-soft mt-1 max-w-2xl text-[13px]">
          Queue an email to an audience — the subscribers of one or more lists, or the rows of an
          uploaded CSV. Either way the audience is snapshotted into the send as queued recipients, so
          editing a list afterwards{' '}
          <span className="text-accent font-semibold">cannot change what goes out.</span>
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-10">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          {/* ── Form ──────────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="surface-row space-y-6 p-5">
            {/* Email */}
            <div>
              <label className="field-label">Email</label>
              <select
                className="field-input w-full cursor-pointer"
                value={emailId}
                onChange={(event) => setEmailId(event.target.value)}
                required
              >
                <option value="">Choose an email…</option>
                {emailRows.map((email) => (
                  <option key={email['id'] as string} value={email['id'] as string}>
                    {(email['subject'] as string) || 'Untitled email'}
                  </option>
                ))}
              </select>
              {emailRows.length === 0 && !emails.isLoading && (
                <p className="text-muted mt-1.5 text-xs">
                  No emails yet — create one under Content first.
                </p>
              )}
            </div>

            {/* Audience */}
            <div>
              <label className="field-label">Audience</label>

              {/* Where the audience comes from. Two shapes of the same send:
                  stored lists, or a file uploaded for this one. */}
              <div className="border-line bg-bg/60 mb-3 inline-flex rounded-full border p-1">
                {(
                  [
                    { id: 'lists', label: 'Lists', icon: Users },
                    { id: 'csv', label: 'CSV file', icon: FileSpreadsheet },
                  ] as const
                ).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setAudienceMode(id)}
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-150 ${
                      audienceMode === id
                        ? 'bg-card text-ink shadow-sm'
                        : 'text-muted hover:text-ink-soft'
                    }`}
                  >
                    <Icon size={13} strokeWidth={2.2} />
                    {label}
                  </button>
                ))}
              </div>

              {audienceMode === 'csv' ? (
                <CsvAudiencePicker
                  file={csvFile}
                  onPick={(file) => {
                    setCsvFile(file);
                    setCsvRowErrors(null);
                    setCsvMissingColumns(null);
                  }}
                  requiredVariables={variableNames.filter((name) => definitions[name]?.required)}
                  rowErrors={csvRowErrors}
                  missingColumns={csvMissingColumns}
                />
              ) : listRows.length === 0 ? (
                <p className="text-muted text-xs">
                  No lists yet — build one under Lists, then import or add subscribers.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {listRows.map((list) => {
                    const checked = listIds.includes(list.id);
                    return (
                      <label
                        key={list.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                          checked
                            ? 'border-accent-border bg-accent/8'
                            : 'border-line hover:bg-ink/5'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleList(list.id)}
                          className="border-line-strong rounded"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="text-ink block truncate text-[13px] font-semibold">
                            {list.name}
                          </span>
                          <span className="text-muted block text-[11px]">
                            {list.subscriberCount.toLocaleString()} subscriber
                            {list.subscriberCount === 1 ? '' : 's'}
                            {Object.keys(list.attributes).length > 0 && (
                              <> · {Object.keys(list.attributes).length} merge field(s)</>
                            )}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {audienceMode === 'lists' && (
                <p className="text-muted mt-2 text-[11px]">
                  A contact who is in more than one selected list is queued once.
                </p>
              )}
            </div>

            {/* Sender — and, underneath it, where a reply to this send goes.
                Both are headers the send is delivered with, and both default
                to the sender row; the override is per send, not per sender,
                because one sender often runs several campaigns whose replies
                belong in different inboxes. */}
            <div>
              <label className="field-label">Send through</label>
              {senderRows.length === 0 ? (
                <p className="text-muted text-xs">
                  No SMTP sender configured — add one under Senders.
                </p>
              ) : (
                <select
                  className="field-input w-full cursor-pointer"
                  value={senderId}
                  onChange={(event) => setSenderId(event.target.value)}
                >
                  {senderRows.map((sender) => {
                    const data = sender.data as Record<string, unknown>;
                    const from = (data['from'] as string) || (data['user'] as string) || '';
                    return (
                      <option key={sender.id} value={sender.id}>
                        {sender.name}
                        {from ? ` — ${from}` : ''}
                        {sender.hasSecret ? '' : ' (no password stored)'}
                      </option>
                    );
                  })}
                </select>
              )}

              <div className="mt-4">
                <label className="field-label" htmlFor="reply-to">
                  Reply-To <span className="text-muted font-normal">— optional</span>
                </label>
                <input
                  id="reply-to"
                  type="email"
                  autoComplete="off"
                  spellCheck={false}
                  className={
                    'field-input w-full' +
                    (replyToError ? ' border-danger/30 focus:border-danger/50' : '')
                  }
                  value={replyTo}
                  placeholder={senderFrom || 'hello@example.com'}
                  onChange={(event) => {
                    setReplyTo(event.target.value);
                    if (replyToError) setReplyToError(null);
                  }}
                />
                <p className="text-muted mt-1.5 text-[11px]">
                  {replyToError ? (
                    <span className="text-danger">{replyToError}</span>
                  ) : replyTo.trim() ? (
                    <>
                      Replies go to{' '}
                      <span className="text-ink-soft font-semibold">{replyTo.trim()}</span> — the
                      From address stays{' '}
                      <span className="text-ink-soft">{senderFrom || 'the sender’s own'}</span>.
                    </>
                  ) : (
                    <>
                      Leave empty and replies go to the From address
                      {senderFrom ? (
                        <>
                          {' '}
                          (<span className="text-ink-soft">{senderFrom}</span>)
                        </>
                      ) : null}
                      . Set an address here when that mailbox is not read — a no-reply sender, for
                      instance.
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Timing */}
            <div>
              <label className="field-label">When</label>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-ink-soft flex cursor-pointer items-center gap-2 text-[13px]">
                  <input
                    type="radio"
                    checked={scheduledInput === ''}
                    onChange={() => setScheduledInput('')}
                    className="border-line-strong"
                  />
                  As soon as possible
                </label>
                <label className="text-ink-soft flex cursor-pointer items-center gap-2 text-[13px]">
                  <input
                    type="radio"
                    checked={scheduledInput !== ''}
                    onChange={() =>
                      setScheduledInput(
                        // Default to an hour out, in the local time the input shows.
                        new Date(Date.now() + 3_600_000 - new Date().getTimezoneOffset() * 60_000)
                          .toISOString()
                          .slice(0, 16),
                      )
                    }
                    className="border-line-strong"
                  />
                  Schedule for
                </label>
                {scheduledInput !== '' && (
                  <input
                    type="datetime-local"
                    className="field-input w-auto"
                    value={scheduledInput}
                    onChange={(event) => setScheduledInput(event.target.value)}
                  />
                )}
              </div>
            </div>

            {/* Variables: per-send constants, which a CSV audience supplies per
                row instead — so this panel belongs to the list mode. */}
            {audienceMode === 'csv' && variableNames.length > 0 && (
              <div className="border-line border-t pt-5">
                <p className="field-label">Merge values</p>
                <p className="text-muted text-xs">
                  A CSV audience carries its own: each column named after a variable fills{' '}
                  <span className="font-mono">{'{{'}</span>that variable
                  <span className="font-mono">{'}}'}</span> per row.{' '}
                  {variableNames
                    .filter((name) => definitions[name]?.required)
                    .map((name) => `{{${name}}}`)
                    .join(', ')}
                  {variableNames.some((name) => definitions[name]?.required) && ' '}
                  {variableNames.some((name) => definitions[name]?.required) ? (
                    <>
                      {variableNames.filter((name) => definitions[name]?.required).length === 1
                        ? 'is required, so the file needs a column for it'
                        : 'are required, so the file needs a column for each'}
                      .
                    </>
                  ) : null}
                </p>
              </div>
            )}

            {audienceMode === 'lists' && variableNames.length > 0 && (
              <div className="border-line border-t pt-5">
                <p className="field-label">
                  Merge values{' '}
                  <span className="text-muted font-normal">
                    — used wherever the audience has no value of its own
                  </span>
                </p>
                <p className="text-muted mb-3 text-xs">
                  This email defines merge fields. A value here applies to every recipient unless
                  their list carries an attribute with the same name. Leave empty if you want the
                  values to come from subscriber attributes only, will skip subscribers without this
                  attribute.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {variableNames.map((name) => {
                    const definition = definitions[name]!;
                    const error = variableErrors[name];
                    const personalised = (previewData?.attributeKeys ?? []).includes(name);
                    const value = variableInputs[name] ?? '';
                    const fieldCls =
                      'field-input' + (error ? ' border-danger/30 focus:border-danger/50' : '');

                    return (
                      <div key={name}>
                        <label className="field-label">
                          {name}
                          {definition.required && <span className="text-danger"> *</span>}
                          {personalised && (
                            <span className="text-accent ml-1.5 text-[10px] font-semibold tracking-wide uppercase">
                              per recipient
                            </span>
                          )}
                        </label>
                        {definition.type === 'boolean' ? (
                          <select
                            className={`${fieldCls} w-full cursor-pointer`}
                            value={value}
                            onChange={(event) =>
                              setVariableInputs((inputs) => ({
                                ...inputs,
                                [name]: event.target.value,
                              }))
                            }
                          >
                            <option value="">— not set —</option>
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        ) : definition.type === 'enum' ? (
                          <select
                            className={`${fieldCls} w-full cursor-pointer`}
                            value={value}
                            onChange={(event) =>
                              setVariableInputs((inputs) => ({
                                ...inputs,
                                [name]: event.target.value,
                              }))
                            }
                          >
                            <option value="">— not set —</option>
                            {definition.enum.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : definition.type === 'list' ? (
                          // A list's value is its items, so the field holds JSON
                          // — multi-line, because one item already is.
                          <textarea
                            rows={3}
                            className={`${fieldCls} font-mono`}
                            value={value}
                            onChange={(event) =>
                              setVariableInputs((inputs) => ({
                                ...inputs,
                                [name]: event.target.value,
                              }))
                            }
                            placeholder={variablePlaceholder(definition)}
                          />
                        ) : (
                          <input
                            className={fieldCls}
                            value={value}
                            onChange={(event) =>
                              setVariableInputs((inputs) => ({
                                ...inputs,
                                [name]: event.target.value,
                              }))
                            }
                            placeholder={variablePlaceholder(definition)}
                          />
                        )}
                        {error && <p className="text-danger mt-1 text-xs">{error}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Feedback */}
            {feedback.kind === 'queued' && (
              <div className="bg-ok/10 text-ok rounded-xl p-3.5 text-[13px]">
                <p className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 size={15} />
                  Queued {feedback.recipients.toLocaleString()} recipient
                  {feedback.recipients === 1 ? '' : 's'}
                  {feedback.scheduledAt ? ` for ${formatWhen(feedback.scheduledAt)}` : ''}.
                </p>
                <p className="text-ok/90 mt-1">
                  {feedback.csv
                    ? `${feedback.csv.rows.toLocaleString()} row(s) from ${feedback.csv.file}${
                        feedback.csv.duplicatesInFile > 0
                          ? `, with ${feedback.csv.duplicatesInFile} repeated address(es) collapsed to one copy each`
                          : ''
                      }. `
                    : feedback.duplicates > 0
                      ? `${feedback.duplicates.toLocaleString()} address(es) were in more than one selected list and were queued once. `
                      : ''}
                  The send goes out in the background; watch it below.
                </p>
                {feedback.csv && (
                  <p className="text-muted mt-1 text-[11.5px]">
                    The contacts were not added to your lists — the audience was temporary and is
                    deleted once this send finishes.
                  </p>
                )}
              </div>
            )}
            {feedback.kind === 'error' && (
              <div className="bg-danger/8 text-danger rounded-xl p-3.5 text-[13px]">
                <p className="flex items-center gap-2 font-semibold">
                  <AlertTriangle size={15} />
                  {feedback.message}
                </p>
              </div>
            )}

            <div className="border-line flex items-center justify-end gap-2 border-t pt-5">
              <button
                type="submit"
                disabled={
                  busy ||
                  !emailId ||
                  !senderId ||
                  (audienceMode === 'csv'
                    ? !csvFile
                    : blocking || listIds.length === 0)
                }
                className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  audienceMode === 'lists' && blocking
                    ? 'Some required merge fields have no value'
                    : undefined
                }
              >
                {busy ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    {audienceMode === 'csv' ? 'Uploading…' : 'Queueing…'}
                  </>
                ) : scheduledInput ? (
                  <>
                    <CalendarClock size={15} />
                    Schedule send
                  </>
                ) : (
                  <>
                    <Send size={15} />
                    {audienceMode === 'csv' ? 'Send to this file' : 'Queue send'}
                  </>
                )}
              </button>
            </div>
          </form>

          {/* ── Summary ───────────────────────────────────────── */}
          <aside className="space-y-4">
            <div className="surface-row p-5">
              <h2 className="text-ink flex items-center gap-2 text-[13px] font-bold">
                <Users size={15} className="text-accent" />
                This send
              </h2>

              {audienceMode === 'csv' ? (
                <div className="mt-3 space-y-2 text-xs">
                  <p className="text-muted">
                    The audience is the file itself. Its columns are matched to this email's merge
                    fields by header name, and every row must carry a usable address and a value for
                    each required variable — otherwise nothing is queued and the rows that need
                    fixing are listed.
                  </p>
                  <p className="text-muted">
                    The contacts go into a temporary audience that never appears under Lists, and is
                    deleted once the send finishes.
                  </p>
                </div>
              ) : !canPreview ? (
                <p className="text-muted mt-3 text-xs">
                  Pick an email and at least one list to see who this would reach.
                </p>
              ) : preview.isError ? (
                <p className="text-danger mt-3 text-xs">
                  {preview.error instanceof Error
                    ? preview.error.message
                    : 'Could not read that audience.'}
                </p>
              ) : preview.isLoading ? (
                <p className="text-muted mt-3 flex items-center gap-2 text-xs">
                  <Loader2 size={13} className="animate-spin" />
                  Counting the audience…
                </p>
              ) : previewData ? (
                <>
                  <p className="text-ink mt-3 text-[28px] leading-none font-bold tracking-tight">
                    {previewData.total.toLocaleString()}
                  </p>
                  <p className="text-muted mt-1 text-[11px] tracking-wide uppercase">
                    distinct recipients
                  </p>

                  <div className="border-line mt-3 space-y-1 border-t pt-3">
                    {previewData.lists.map((list) => (
                      <div key={list.id} className="flex items-center justify-between text-xs">
                        <span className="text-ink-soft truncate">{list.name}</span>
                        <span className="text-muted ml-2 shrink-0">
                          {list.subscriberCount.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>

                  {previewData.attributeKeys.length > 0 && (
                    <p className="text-muted mt-3 text-[11px]">
                      Personalised per recipient with{' '}
                      <span className="text-ink-soft">{previewData.attributeKeys.join(', ')}</span>.
                    </p>
                  )}

                  {blocking && (
                    <div className="bg-warn/10 text-ink-soft mt-3 rounded-lg p-3 text-[12px]">
                      <p className="flex items-center gap-1.5 font-semibold">
                        <Lock size={13} />
                        Nothing would fill these
                      </p>
                      <p className="mt-1">
                        {missingVariables.join(', ')} — set a value on the left, or give a
                        subscriber in the selected lists the matching attribute. A list merely
                        defining it is not enough to fill it.
                      </p>
                    </div>
                  )}
                </>
              ) : null}

              {/* The override belongs in the summary as well as the form: it is
                  part of what this send is, and History shows it afterwards. */}
              {replyTo.trim() && (
                <p className="text-muted border-line mt-3 border-t pt-3 text-[11px]">
                  Replies go to{' '}
                  <span className="text-ink-soft font-semibold">{replyTo.trim()}</span>.
                </p>
              )}
            </div>

            {/* ── Recent sends ────────────────────────────────── */}
            <div className="surface-row p-5">
              <h2 className="text-ink flex items-center gap-2 text-[13px] font-bold">
                <Mail size={15} className="text-accent" />
                Recent sends
              </h2>

              {(recent.data?.data ?? []).length === 0 ? (
                <p className="text-muted mt-3 text-xs">Nothing has been sent yet.</p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {(recent.data?.data ?? []).map((send) => (
                    <li key={send.id} className="border-line border-b pb-3 last:border-0 last:pb-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0">
                          <span className="text-ink block truncate text-[12.5px] font-semibold">
                            {send.emailSubject}
                          </span>
                          <span className="text-muted mt-0.5 block text-[11px]">
                            {send.recipients.total.toLocaleString()} recipient
                            {send.recipients.total === 1 ? '' : 's'} ·{' '}
                            {send.scheduledAt && !send.startedAt
                              ? `scheduled ${formatWhen(send.scheduledAt)}`
                              : formatWhen(send.createdAt)}
                          </span>
                        </span>
                        <span className={`badge ${STATUS_CLASS[send.status]}`}>{send.status}</span>
                      </div>

                      <div className="text-muted mt-1 flex items-center gap-2 text-[11px]">
                        <span>{send.recipients.delivered.toLocaleString()} delivered</span>
                        {send.recipients.failed > 0 && (
                          <span className="text-danger">
                            {send.recipients.failed.toLocaleString()} failed
                          </span>
                        )}
                        {send.recipients.queued > 0 && (
                          <span>{send.recipients.queued.toLocaleString()} queued</span>
                        )}
                      </div>

                      {(send.status === 'queued' || send.status === 'sending') && (
                        <button
                          type="button"
                          onClick={() => cancelSend.mutate(send.id)}
                          disabled={cancelSend.isPending}
                          className="text-danger mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold hover:underline disabled:opacity-50"
                        >
                          <Ban size={11} />
                          Cancel
                        </button>
                      )}

                      {/* A stopped send can be put back in the queue: only the
                          recipients still waiting are delivered. */}
                      {send.status === 'cancelled' && !send.completedAt && (
                        <button
                          type="button"
                          onClick={() => resumeSend.mutate(send.id)}
                          disabled={resumeSend.isPending}
                          className="text-accent mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold hover:underline disabled:opacity-50"
                        >
                          <Play size={11} />
                          Resume
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CSV audience picker
//
// A file input, plus what the server said when it refused the last one. The
// refusals are the interesting half: `errors` lists the rows that could not be
// sent (with the row number and why) and `missingVariables` names a required
// variable the file has no column for — both come straight from the validator
// that guards the public API, so the editor cannot promise something the API
// would reject.
// ─────────────────────────────────────────────────────────────────
function CsvAudiencePicker({
  file,
  onPick,
  requiredVariables,
  rowErrors,
  missingColumns,
}: {
  file: File | null;
  onPick: (file: File | null) => void;
  /** Variables the email marks required — the file needs a column for each. */
  requiredVariables: string[];
  rowErrors: CsvSendRowError[] | null;
  missingColumns: string[] | null;
}) {
  return (
    <div className="space-y-3">
      {file ? (
        <div className="border-line bg-bg/60 flex items-center gap-3 rounded-xl border px-3 py-2.5">
          <FileSpreadsheet size={16} className="text-accent shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="text-ink block truncate text-[13px] font-semibold">{file.name}</span>
            <span className="text-muted block text-[11px]">
              {(file.size / 1024).toFixed(1)} KB
            </span>
          </span>
          <button
            type="button"
            onClick={() => onPick(null)}
            aria-label="Remove the selected file"
            className="btn-ghost h-8 w-8 rounded-lg px-0"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <label className="border-line-strong hover:border-accent-border hover:bg-accent/5 flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border border-dashed px-4 py-6 text-center transition-colors">
          <Upload size={18} className="text-muted" />
          <span className="text-ink text-[13px] font-semibold">Choose a CSV file</span>
          <span className="text-muted text-[11.5px]">
            One row per recipient · an email column · a column for each merge field
          </span>
          <input
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={(event) => onPick(event.target.files?.[0] ?? null)}
          />
        </label>
      )}

      <p className="text-muted text-[11.5px]">
        Columns are matched to this email&apos;s merge fields by header name.
        {requiredVariables.length > 0 && (
          <>
            {' '}
            This email requires{' '}
            <span className="text-ink-soft font-semibold">
              {requiredVariables.map((name) => `{{${name}}}`).join(', ')}
            </span>{' '}
            — the file needs a column for each, with a value on every row.
          </>
        )}
      </p>

      {missingColumns && (
        <div className="bg-danger/8 text-danger rounded-xl p-3 text-[12.5px]">
          <p className="flex items-center gap-1.5 font-semibold">
            <AlertTriangle size={14} />
            The file has no column for{' '}
            {missingColumns.map((name) => `{{${name}}}`).join(', ')}
          </p>
          <p className="mt-1 opacity-90">
            Add the column{missingColumns.length === 1 ? '' : 's'} (or rename the header) and upload
            again. Nothing was queued.
          </p>
        </div>
      )}

      {rowErrors && rowErrors.length > 0 && (
        <div className="bg-danger/8 text-danger rounded-xl p-3 text-[12.5px]">
          <p className="flex items-center gap-1.5 font-semibold">
            <AlertTriangle size={14} />
            {rowErrors.length === 1
              ? 'One row cannot be sent'
              : `${rowErrors.length} rows cannot be sent`}
          </p>
          <p className="mt-1 opacity-90">
            Every recipient must have a usable address and a value for each required merge field.
            Nothing was queued — fix the file and upload it again.
          </p>
          <ul className="mt-2 space-y-0.5 font-mono text-[11px]">
            {rowErrors.slice(0, 8).map((error) => (
              <li key={`${error.row}-${error.reason}`}>
                row {error.row}
                {error.email ? ` · ${error.email}` : ''} — {error.message ?? error.reason}
              </li>
            ))}
            {rowErrors.length > 8 && <li>…and {rowErrors.length - 8} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
